import React, { useState, useEffect, useRef, useCallback } from 'react';

function Bubble({ delay, size, left }) {
  return (
    <div
      className="absolute rounded-full opacity-0"
      style={{
        width: size,
        height: size,
        left: `${left}%`,
        bottom: '-60px',
        background: `radial-gradient(circle at 30% 30%, rgba(245,168,0,0.25), rgba(0,51,102,0.15))`,
        animation: `bubbleRise ${8 + Math.random() * 6}s ease-in-out ${delay}s infinite`,
      }}
    />
  );
}

export default function Screensaver({ timeoutMinutes = 5 }) {
  const [active, setActive] = useState(false);
  const timerRef = useRef(null);
  const logoRef = useRef({ x: 100, y: 100, dx: 1.5, dy: 1.2 });
  const animRef = useRef(null);
  const containerRef = useRef(null);

  const resetTimer = useCallback(() => {
    if (active) setActive(false);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setActive(true), timeoutMinutes * 60 * 1000);
  }, [timeoutMinutes, active]);

  useEffect(() => {
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach(e => window.addEventListener(e, resetTimer));
    resetTimer();
    return () => {
      events.forEach(e => window.removeEventListener(e, resetTimer));
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [resetTimer]);

  // DVD-style logo bounce animation
  useEffect(() => {
    if (!active || !containerRef.current) {
      if (animRef.current) cancelAnimationFrame(animRef.current);
      return;
    }
    const logoEl = containerRef.current.querySelector('.screensaver-logo');
    if (!logoEl) return;

    const logoW = 180, logoH = 90;
    const state = logoRef.current;

    const animate = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;

      state.x += state.dx;
      state.y += state.dy;

      if (state.x <= 0 || state.x + logoW >= w) {
        state.dx *= -1;
        state.x = Math.max(0, Math.min(state.x, w - logoW));
      }
      if (state.y <= 0 || state.y + logoH >= h) {
        state.dy *= -1;
        state.y = Math.max(0, Math.min(state.y, h - logoH));
      }

      logoEl.style.transform = `translate(${state.x}px, ${state.y}px)`;
      animRef.current = requestAnimationFrame(animate);
    };

    animRef.current = requestAnimationFrame(animate);
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [active]);

  if (!active) return null;

  const bubbles = Array.from({ length: 15 }, (_, i) => ({
    delay: i * 0.7,
    size: `${20 + Math.random() * 40}px`,
    left: Math.random() * 100,
  }));

  return (
    <div
      ref={containerRef}
      data-testid="screensaver-overlay"
      className="fixed inset-0 z-[9999] cursor-none overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #001a2e 0%, #003366 50%, #001a38 100%)' }}
      onClick={resetTimer}
    >
      {/* Bubbles */}
      {bubbles.map((b, i) => <Bubble key={i} {...b} />)}

      {/* Bouncing logo */}
      <div className="screensaver-logo absolute" style={{ width: 180, height: 90 }}>
        <img
          src="/tw1-logo.png"
          alt="TW1 Group"
          className="w-full h-full object-contain drop-shadow-2xl"
          style={{ filter: 'drop-shadow(0 0 20px rgba(245,168,0,0.4))' }}
        />
      </div>

      {/* Subtle text */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/20 text-xs tracking-widest uppercase">
        Beweeg muis om terug te keren
      </div>

      <style>{`
        @keyframes bubbleRise {
          0% { opacity: 0; transform: translateY(0) scale(0.5); }
          10% { opacity: 0.6; }
          50% { opacity: 0.3; }
          100% { opacity: 0; transform: translateY(-100vh) scale(1.2); }
        }
      `}</style>
    </div>
  );
}
